import * as React from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Drawer from '@mui/material/Drawer';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import { TranslatePlain } from '../../../shared/LanguageService';
import FailureSnackbar from '../../../shared/feedback/FailureSnackbar';
import { translationEditorContainer } from '../state/renderedTranslations';
import { editTranslationsShortcutLabel } from '../state/useEditTranslationsShortcut';
import { useTranslationDrawer } from '../state/useTranslationDrawer';
import TranslationForm from './TranslationForm';

const drawerWidth = 420;

function TranslationEditorDrawer() {
    const {
        editTranslationsEnabled,
        showTranslationKeys,
        setShowTranslationKeys,
        showNotifications,
        setShowNotifications,
        selectedKey,
        disableEditTranslations,
        cancelEditing,
        finishEditing,
        translation,
        retryLoading,
        failureMessage,
        reportFailure,
        dismissFailure
    } = useTranslationDrawer();

    return (
        <Drawer
            anchor="right"
            variant="temporary"
            open={editTranslationsEnabled}
            hideBackdrop
            disableEnforceFocus
            disableAutoFocus
            disableRestoreFocus
            disableScrollLock
            ModalProps={{ container: translationEditorContainer }}
            sx={{ pointerEvents: 'none', zIndex: (theme) => theme.zIndex.modal + 1 }}
            PaperProps={{
                sx: {
                    pointerEvents: 'auto',
                    width: drawerWidth,
                    padding: 2,
                    boxSizing: 'border-box'
                }
            }}
            data-translation-editor
        >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="h6" component="h2">
                    {TranslatePlain('translation-editor/title')}
                </Typography>
                <IconButton
                    aria-label={TranslatePlain('translation-editor/button/close')}
                    onClick={disableEditTranslations}
                >
                    <CloseIcon />
                </IconButton>
            </Box>
            <FormControlLabel
                control={
                    <Switch
                        checked={showTranslationKeys}
                        onChange={(event, checked) => setShowTranslationKeys(checked)}
                    />
                }
                label={TranslatePlain('translation-editor/toggle/show-keys')}
            />
            <FormControlLabel
                control={
                    <Switch
                        checked={showNotifications}
                        onChange={(event, checked) => setShowNotifications(checked)}
                    />
                }
                label={TranslatePlain('translation-editor/toggle/show-notifications')}
            />
            {selectedKey === null ? (
                <Typography variant="body2" sx={{ marginTop: 2 }}>
                    {TranslatePlain('translation-editor/empty-state')}
                </Typography>
            ) : null}
            {selectedKey !== null && translation.isFetching && !translation.data ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: 2 }}>
                    <CircularProgress size={24} />
                </Box>
            ) : null}
            {selectedKey !== null && translation.error && !translation.isFetching ? (
                <Alert
                    severity="error"
                    sx={{ marginTop: 2 }}
                    action={
                        <Button color="inherit" size="small" onClick={retryLoading}>
                            {TranslatePlain('translation-editor/button/retry')}
                        </Button>
                    }
                >
                    {TranslatePlain('translation-editor/error/load-failed')}
                    <Typography variant="body2">{translation.error.message}</Typography>
                </Alert>
            ) : null}
            {selectedKey !== null && translation.data ? (
                <TranslationForm
                    key={`${selectedKey}:${translation.data.translationsVersion}`}
                    translation={translation.data}
                    onSaved={finishEditing}
                    onCancel={cancelEditing}
                    onFailed={reportFailure}
                />
            ) : null}
            <Typography variant="caption" color="text.secondary" sx={{ marginTop: 'auto' }}>
                {TranslatePlain('translation-editor/helper/reload-note')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
                {TranslatePlain('translation-editor/shortcut', {
                    shortcut: editTranslationsShortcutLabel
                })}
            </Typography>
            <FailureSnackbar
                open={failureMessage !== null}
                onClose={dismissFailure}
                text={failureMessage || ''}
            />
        </Drawer>
    );
}

export default TranslationEditorDrawer;
